import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import ScorecardNavigation from './ScorecardNavigation';
import ErrorMessage from '../common/ErrorMessage';
import LoadingSpinner from '../common/LoadingSpinner';
import { storeShiftDetailsData } from '../../data/scorecardData';
import { config } from '../../config/environment';
import ShiftMetricsTable, { type ShiftMetricsRow } from './ShiftMetricsTable';
import {
  getStoreShifts,
  type StoreShiftRow,
  type StoreShiftsGroup,
  type MetricColumn,
  ServerScorecardForbiddenError,
} from '../../services/serverScorecardService';
import { RoleBand } from './RoleGridHeading';
import { groupsOf, visibleGroups, showRoleHeadings } from '../../services/scorecardGroups';
import * as Sentry from '@sentry/react';

interface StoreShiftDetailsProps {
  date?: string;
  shift?: string;
  score?: number;
  manager?: string;
  store?: string;
  shiftId?: string;
}

const StoreShiftDetails: React.FC<StoreShiftDetailsProps> = ({
  date = 'September 15, 2024',
  shift = 'Dinner',
  score = 92,
  manager = 'Sarah Johnson',
  store = 'Fremont',
  shiftId = ''
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { metricLabels, selectedTenant } = useTenant();

  // Get shift data from location state or use defaults
  const shiftData = location.state?.shiftData || { date, shift, score, manager, store, shiftId };

  // feat-093 Slice C: per-server rows for THIS manager shift come from the
  // backend GET /api/server-scorecard/{tenant}/shifts?shift_id=<managerShiftId>
  // endpoint. Score, visit_count and per-metric percents are all backend-computed
  // and rendered VERBATIM — the old per-manager shifts fetch + legacy POST + raw
  // POS query + client recompute are gone (feedback_metrics_single_source; C3/C4).
  //
  // feat-317 (ADR §7.3): one group per effective role, each with its own
  // columns and rows. An old backend (no `groups`) → one legacy group.
  const [groups, setGroups] = useState<StoreShiftsGroup[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [storeLocation, setStoreLocation] = useState<string>('');

  const tenantId = selectedTenant || user?.managementUser?.tenant_id || undefined;

  // Fetch backend-scored server rows for this manager shift.
  useEffect(() => {
    const fetchShiftRows = async () => {
      if (shiftData.store) {
        setStoreLocation(shiftData.store);
      }
      if (config.useDummyData) {
        // Dev/fake: render the canned dummy data for this date/shift/store as
        // backend-shaped rows (no client math). storeShiftDetailsData carries
        // pre-baked per-server numbers used only for the no-backend demo.
        const shiftDetails = storeShiftDetailsData.find(
          (entry) => entry.date === shiftData.date && entry.shift === shiftData.shift && entry.store === shiftData.store
        );
        const cols: MetricColumn[] = [
          { key: 'metric_1', label: 'Steak Shawarma Upsell' },
          { key: 'metric_2', label: 'Döner Kebab Upsell' },
          { key: 'metric_3', label: 'Dubai Chocolate Upsell' },
        ];
        const dummyRows: StoreShiftRow[] = (shiftDetails?.servers || []).map((s, i) => ({
          shift_id: `dummy-${i}`,
          manager_shift_id: 'dummy-mgr-shift',
          server_id: `dummy-srv-${i}`,
          server_name: s.server,
          manager_id: 'dummy-mgr',
          manager_name: shiftData.manager || 'Manager',
          location_id: 'dummy-loc',
          location_name: shiftData.store || 'Store',
          shift_date: shiftData.date,
          shift_label: shiftData.shift,
          score_status: 'scored',
          score: s.score,
          score_display: `${s.score}`,
          scored: true,
          visit_count: s.greetings,
          greetings: s.greetings,
          metrics: {
            metric_1: { percent: s.premiumMenu, count: null, present: true },
            metric_2: { percent: s.itemMention, count: null, present: true },
            metric_3: { percent: s.itemDescription, count: null, present: true },
          },
          drink: { percent: s.drinkPromotions, count: null, present: true },
        }));
        setGroups([
          {
            role: null,
            display_name: null,
            metric_columns: cols,
            drink_column: { key: 'drink_promotion', label: 'Dubai Chocolate' },
            rows: dummyRows,
          },
        ]);
        return;
      }
      if (!shiftData.shiftId || !tenantId) {
        setGroups((prev) => prev.map((g) => ({ ...g, rows: [] })));
        return;
      }
      setIsLoading(true);
      setError(null);
      try {
        const resp = await getStoreShifts(tenantId, { shiftId: shiftData.shiftId });
        setGroups(
          groupsOf(resp, (r): StoreShiftsGroup => ({
            role: null,
            display_name: null,
            metric_columns: r.metric_columns || [],
            drink_column: r.drink_column || null,
            rows: r.rows || [],
          })),
        );
      } catch (err: any) {
        if (err instanceof ServerScorecardForbiddenError) {
          setGroups((prev) => prev.map((g) => ({ ...g, rows: [] })));
        } else {
          console.error('StoreShiftDetails - Error fetching server contributions:', err);
          Sentry.captureException(err);
          setError(err.message || 'Failed to fetch server contributions');
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchShiftRows();
  }, [shiftData.shiftId, shiftData.date, shiftData.shift, shiftData.store, tenantId]);

  // feat-317: every role group's rows. The Shift Score tile averages ALL of
  // them — one number across roles (spec round-3 Q1).
  const storeShiftRows: StoreShiftRow[] = useMemo(() => groups.flatMap((g) => g.rows), [groups]);
  // Groups with rows, in response order; all empty → the first, emptied.
  const tableGroups = useMemo(() => visibleGroups(groups, (g) => g.rows), [groups]);

  // Display-average of the BACKEND per-shift scores (averaging backend numbers,
  // not re-deriving a rate — allowed under single-source).
  const averageShiftScore = useMemo(() => {
    const scored = storeShiftRows.filter((r) => r.score !== null && r.score !== undefined);
    if (scored.length === 0) return shiftData.score;
    const totalScore = scored.reduce((sum, r) => sum + (r.score as number), 0);
    return Math.round(totalScore / scored.length);
  }, [storeShiftRows, shiftData.score]);

  // Current shift details with real or dummy data
  const currentShiftDetails = {
    date: shiftData.date,
    shift: shiftData.shift,
    score: averageShiftScore,
    manager: shiftData.manager,
    store: shiftData.store,
    servers: storeShiftRows,
  };

  // Build render rows from backend values — every numeric field is verbatim.
  // feat-317: per group, against that group's own columns.
  const buildTableRows = (group: StoreShiftsGroup): ShiftMetricsRow[] => {
    const metricColumns: MetricColumn[] = group.metric_columns || [];
    const drinkColumn: MetricColumn | null = group.drink_column || null;
    return group.rows.map((row) => {
      const metricValues: Record<string, number> = {};
      for (const col of metricColumns) {
        const cell = row.metrics[col.key];
        metricValues[col.key] =
          cell && cell.percent !== null && cell.percent !== undefined ? cell.percent : 0;
      }
      if (drinkColumn) {
        metricValues[drinkColumn.key] =
          row.drink && row.drink.percent !== null && row.drink.percent !== undefined
            ? row.drink.percent
            : 0;
      }
      return {
        serverName: row.server_name,
        type: 'all',
        greetings: row.greetings ?? 0,
        visitCount: row.visit_count ?? row.greetings ?? undefined,
        score: row.score ?? 0,
        premiumMenu: 0,
        itemMention: 0,
        itemDescription: 0,
        drinkPromotions: 0,
        metricValues,
      };
    });
  };

  const renderTable = (group: StoreShiftsGroup) => (
    <ShiftMetricsTable
      shifts={buildTableRows(group)}
      showServer={true}
      showShift={false}
      showGreetings={true}
      metricLabels={metricLabels}
      dynamicMetrics={(group.metric_columns || []).map((c) => ({ key: c.key, label: c.label } as any))}
      dynamicDrinkLabel={group.drink_column ? group.drink_column.label : null}
      emptyMessage="No server data available for this shift."
    />
  );

  const handleBackToStoreScorecard = () => {
    // Restore the filter state that was saved when navigating to details
    const savedFilters = location.state?.filters;
    navigate('/restaurant/store-scorecard', { 
      state: { 
        storeData: { 
          storeName: currentShiftDetails.store 
        },
        filters: savedFilters || {}
      } 
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <ScorecardNavigation />
        <div className="p-6">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <ScorecardNavigation />
        <div className="p-6">
          <ErrorMessage message={error} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <ScorecardNavigation />
      
      <div className="p-6">
        {/* Back Button */}
        <div className="mb-6">
          <button
            onClick={handleBackToStoreScorecard}
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
          >
            <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Store Scorecard
          </button>
        </div>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Store Shift Details</h1>
        </div>

        {/* Stats Blocks */}
        <div className="grid grid-cols-3 gap-6 mb-8">
          {/* Date and Shift Block */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">
              Date & Shift
            </div>
            <div className="flex items-center gap-3">
              <div className="text-lg font-semibold text-gray-900">
                {currentShiftDetails.date}
              </div>
              <span className={`inline-flex px-2 py-1 text-lg font-semibold rounded-full ${
                currentShiftDetails.shift === 'Dinner' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
              }`}>
                {currentShiftDetails.shift}
              </span>
            </div>
          </div>

          {/* Manager Block */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">
              Manager
            </div>
            <div className="text-lg font-semibold text-gray-900">
              {currentShiftDetails.manager}
            </div>
          </div>

          {/* Shift Score Block */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">
              Shift Score
            </div>
            <div className="text-2xl font-bold text-gray-900">
              <span className={`inline-flex px-3 py-1 text-lg font-semibold rounded-full ${
                currentShiftDetails.score >= 95 ? 'bg-green-100 text-green-800' :
                currentShiftDetails.score >= 90 ? 'bg-yellow-100 text-yellow-800' :
                currentShiftDetails.score >= 85 ? 'bg-orange-100 text-orange-800' :
                'bg-red-100 text-red-800'
              }`}>
                {currentShiftDetails.score}
              </span>
            </div>
          </div>
        </div>

        {/* Store Information */}
        <div className="mb-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">
              Store Location
            </div>
            <div className="text-lg font-semibold text-gray-900">
              {storeLocation || currentShiftDetails.store}
            </div>
          </div>
        </div>

        {/* Servers Contributing to the Shift Score Table */}
        <div className="bg-white shadow-sm rounded-lg border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Servers Contributing to the Shift Score</h2>
          </div>
          
          {currentShiftDetails.servers.length > 0 && showRoleHeadings(tableGroups) ? (
            // feat-317 (design NOTES §4.4): a role band + table per group.
            <div>
              {tableGroups.map((g, i) => (
                <div key={g.role ?? `group-${i}`}>
                  <RoleBand displayName={g.display_name} role={g.role} first={i === 0} />
                  {renderTable(g)}
                </div>
              ))}
            </div>
          ) : currentShiftDetails.servers.length > 0 ? (
            renderTable(tableGroups[0])
          ) : (
            <div className="px-6 py-12 text-center">
              <div className="text-gray-500 text-sm">
                No server data available for this shift.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StoreShiftDetails;
